import React, { useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useMutation, useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlusCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";

import { COLOR_APP } from "../../../constants";
import { CF_PERMISSION_GROUPS, CF_PERMISSIONS } from "../../../constants/permissions";
import { GET_CF_ROLES } from "../../../graphql/staff/query";
import { CREATE_CF_ROLE, DELETE_CF_ROLE, UPDATE_CF_ROLE } from "../../../graphql/staff/mutation";
import { errorSwal, successSwal } from "../../../helpers/sweetalert";
import LoadingSpinner from "../../../components/LoadingSpinner";

const PERMISSION_LABEL = Object.fromEntries(
  CF_PERMISSION_GROUPS.flatMap((g) =>
    g.items.flatMap((i) => [
      [i.value, i.name],
      ...(i.children || []).map((c) => [c.value, `${i.name}: ${c.name}`]),
    ]),
  ),
);
//* ສິດຍ່ອຍ → ສິດຫຼັກ (ເຊັ່ນ PRODUCTS_CREATE → PRODUCTS)
const PARENT_OF = Object.fromEntries(
  CF_PERMISSION_GROUPS.flatMap((g) =>
    g.items.flatMap((i) => (i.children || []).map((c) => [c.value, i.value])),
  ),
);
const CHILDREN_OF = Object.fromEntries(
  CF_PERMISSION_GROUPS.flatMap((g) =>
    g.items.map((i) => [i.value, (i.children || []).map((c) => c.value)]),
  ),
);

const EMPTY_FORM = { id: null, name: "", note: "", permissions: [] };

export default function SettingsRole() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [isOpen, setIsOpen] = useState(false);

  const { data, loading, refetch } = useQuery(GET_CF_ROLES, { fetchPolicy: "network-only" });
  const [createRole, { loading: creating }] = useMutation(CREATE_CF_ROLE);
  const [updateRole, { loading: updating }] = useMutation(UPDATE_CF_ROLE);
  const [deleteRole] = useMutation(DELETE_CF_ROLE);

  const roles = data?.cfRoles?.dataMany ?? [];
  const saving = creating || updating;
  const isAllChecked = CF_PERMISSIONS.every((p) => form.permissions.includes(p));

  const openForm = (role) => {
    setForm(
      role
        ? { id: role.id, name: role.name ?? "", note: role.note ?? "", permissions: role.permissions ?? [] }
        : EMPTY_FORM,
    );
    setIsOpen(true);
  };

  const closeForm = () => {
    setIsOpen(false);
    setForm(EMPTY_FORM);
  };

  //* ຕິກສິດຍ່ອຍ → ຕິກສິດຫຼັກໃຫ້ນຳ; ເອົາສິດຫຼັກອອກ → ສິດຍ່ອຍອອກນຳ
  const togglePermission = (value) =>
    setForm((f) => {
      if (f.permissions.includes(value)) {
        const drop = [value, ...(CHILDREN_OF[value] || [])];
        return { ...f, permissions: f.permissions.filter((p) => !drop.includes(p)) };
      }
      const parent = PARENT_OF[value];
      const add = parent && !f.permissions.includes(parent) ? [value, parent] : [value];
      return { ...f, permissions: [...f.permissions, ...add] };
    });

  const handleSave = async () => {
    if (saving || !form.name.trim()) return;
    const variables = {
      data: { name: form.name.trim(), note: form.note, permissions: form.permissions },
    };
    try {
      const res = form.id
        ? (await updateRole({ variables: { ...variables, id: form.id } }))?.data?.updateCfRole
        : (await createRole({ variables }))?.data?.createCfRole;
      if (!res?.status) return errorSwal(res?.msg);
      successSwal(form.id ? "ແກ້ໄຂສຳເລັດ" : "ເພີ່ມສຳເລັດ");
      closeForm();
      refetch();
    } catch (err) {
      errorSwal();
    }
  };

  const handleDelete = async (role) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: `ລົບບົດບາດ "${role.name}"?`,
      showCancelButton: true,
      confirmButtonText: "ລົບ",
      cancelButtonText: "ຍົກເລີກ",
      confirmButtonColor: COLOR_APP,
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = (await deleteRole({ variables: { id: role.id } }))?.data?.deleteCfRole;
      if (!res?.status) return errorSwal(res?.msg);
      successSwal("ລົບສຳເລັດ");
      refetch();
    } catch (err) {
      errorSwal();
    }
  };

  return (
    <div>

      <div className="d-flex justify-content-between align-items-center">
        <h5 style={{ fontWeight: "bold", margin: 0 }}>ຈັດການບົດບາດ</h5>
        <Button style={{ background: COLOR_APP, border: "none" }} onClick={() => openForm(null)}>
          <FontAwesomeIcon icon={faPlusCircle} /> ເພີ່ມບົດບາດ
        </Button>
      </div>

      <Card style={{ marginTop: 16 }}>
        <Card.Body>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>ບົດບາດ</th>
                  <th>ສິດທີ່ໄດ້ຮັບ</th>
                  <th>ໝາຍເຫດ</th>
                  <th style={{ textAlign: "right" }}>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", color: "#999" }}>
                      ຍັງບໍ່ມີບົດບາດ
                    </td>
                  </tr>
                )}
                {roles.map((role, index) => (
                  <tr key={role.id}>
                    <td>{index + 1}</td>
                    <td style={{ fontWeight: "bold" }}>{role.name}</td>
                    <td style={{ maxWidth: 420 }}>
                      {CF_PERMISSIONS.every((p) => (role.permissions ?? []).includes(p))
                        ? "ທຸກເມນູ"
                        : (role.permissions ?? [])
                            .filter((p) => PERMISSION_LABEL[p])
                            .map((p) => PERMISSION_LABEL[p])
                            .join(", ") || "-"}
                    </td>
                    <td>{role.note || "-"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Button size="sm" variant="warning" onClick={() => openForm(role)}>
                        <FontAwesomeIcon icon={faPen} />
                      </Button>{" "}
                      <Button size="sm" variant="danger" onClick={() => handleDelete(role)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={isOpen} onHide={closeForm} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: "bold" }}>
            {form.id ? "ແກ້ໄຂບົດບາດ" : "ເພີ່ມບົດບາດ"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Row>
            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>ຊື່ບົດບາດ *</Form.Label>
                <Form.Control
                  value={form.name}
                  placeholder="ເຊັ່ນ: ພະນັກງານແພັກເຄື່ອງ"
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={7}>
              <Form.Group className="mb-3">
                <Form.Label>ໝາຍເຫດ</Form.Label>
                <Form.Control
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Check
            id="permission-all"
            type="checkbox"
            label={<b>ເຂົ້າເຖິງທຸກຢ່າງໃນລະບົບ</b>}
            checked={isAllChecked}
            onChange={(e) =>
              setForm({ ...form, permissions: e.target.checked ? [...CF_PERMISSIONS] : [] })
            }
            className="mb-3"
          />

          {CF_PERMISSION_GROUPS.map((group, gIndex) => (
            <div key={group.title} className="mb-3">
              <div style={{ fontWeight: "bold", marginBottom: 6 }}>
                {gIndex + 1}. {group.title}
              </div>
              {group.items.map((item) => (
                <React.Fragment key={item.value}>
                  <Form.Check
                    id={`permission-${item.value}`}
                    type="checkbox"
                    label={item.name}
                    checked={form.permissions.includes(item.value)}
                    onChange={() => togglePermission(item.value)}
                  />
                  {item.children?.length > 0 && (
                    <div className="d-flex flex-wrap gap-3 mb-1" style={{ paddingLeft: 28 }}>
                      {item.children.map((child) => (
                        <Form.Check
                          key={child.value}
                          id={`permission-${child.value}`}
                          type="checkbox"
                          label={child.name}
                          checked={form.permissions.includes(child.value)}
                          onChange={() => togglePermission(child.value)}
                        />
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closeForm}>ຍົກເລີກ</Button>
          <Button
            style={{ background: COLOR_APP, border: "none" }}
            disabled={!form.name.trim() || saving}
            onClick={handleSave}
          >
            {saving && <Spinner size="sm" />} {form.id ? "ຢືນຢັນແກ້ໄຂ" : "ຢືນຢັນ"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
